<template>
  <div class="layout-sidebar" :class="{ collapsed }">
    <div class="sidebar-header">
      <h4 v-show="!collapsed" class="sidebar-title">{{ title }}</h4>
      <t-button
        variant="text"
        shape="square"
        size="small"
        @click="$emit('toggle-collapse')"
      >
        <t-icon :name="collapsed ? 'chevron-right' : 'chevron-left'" />
      </t-button>
    </div>
    
    <div class="sidebar-content">
      <t-menu
        :value="activeMenu"
        :collapsed="collapsed"
        v-model:expanded="expandedMenus"
        @change="handleMenuChange"
      >
        <template v-for="item in menuItems" :key="item.path">
          <!-- 有子菜单的项 -->
          <t-submenu
            v-if="item.children?.length"
            :value="item.path"
            :title="item.title"
          >
            <template #icon>
              <t-icon :name="item.icon" />
            </template>
            <t-menu-item
              v-for="child in item.children"
              :key="child.path"
              :value="child.path"
              :disabled="child.disabled"
            >
              {{ child.title }}
            </t-menu-item>
          </t-submenu>
          
          <!-- 普通菜单项 -->
          <t-menu-item
            v-else
            :value="item.path"
            :disabled="item.disabled"
          >
            <template #icon>
              <t-icon :name="item.icon" />
            </template>
            {{ item.title }}
          </t-menu-item>
        </template>
      </t-menu>
    </div>
    
    <div v-if="!collapsed && merchantInfo" class="sidebar-footer">
      <div class="merchant-info">
        <div class="merchant-name">{{ merchantInfo.merchantName }}</div>
        <div v-if="merchantInfo.shopName" class="merchant-subtext">
          分支组织：{{ merchantInfo.shopName }}
        </div>
        <div class="merchant-subtext">
          订阅截止：{{ merchantInfo.subscriptionText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import dayjs from 'dayjs'
import { USER_TYPES } from '@/constants/userRoles'

interface MenuItem {
  path: string
  title: string
  icon: string
  disabled?: boolean
  children?: MenuItem[]
}

interface Props {
  collapsed?: boolean
  title?: string
}

interface Emits {
  (e: 'toggle-collapse'): void
}

withDefaults(defineProps<Props>(), {
  collapsed: false,
  title: '导航菜单'
})

const emit = defineEmits<Emits>()

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { user, userType } = storeToRefs(authStore)
const expandedMenus = ref<string[]>([])

const merchantInfo = computed(() => {
  const currentUser = user.value
  if (!currentUser?.merchantId) return null

  const subscriptionText = currentUser.subscriptionEndDate
    ? dayjs(currentUser.subscriptionEndDate).format('YYYY-MM-DD')
    : '未设置'

  return {
    merchantName: currentUser.merchantName || '未命名组织',
    shopName: currentUser.shopName || '',
    subscriptionText,
  }
})

const modules = computed(() => {
  const permissionCodes = user.value?.permissionCodes
  if (Array.isArray(permissionCodes)) {
    return permissionCodes.filter((code) => code.startsWith('module_'))
  }
  return user.value?.enabledModuleIds || []
})
const hasModule = (code: string) => modules.value.includes(code)

const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = []
  const ruleChildren: MenuItem[] = [
    { path: '/merchant/rules/points', title: '积分规则', icon: 'money-circle' },
    { path: '/merchant/rules/members', title: '会员规则', icon: 'crown' },
    { path: '/merchant/rules/discount', title: '折扣规则', icon: 'percentage' },
    { path: '/merchant/rules/wechat', title: '微信配置', icon: 'logo-wechat' },
  ]

  if (userType.value === USER_TYPES.PLATFORM_ADMIN) {
    items.push({
      path: '/platform',
      title: '平台管理',
      icon: 'view-module',
      children: [
        { path: '/platform/merchants', title: '组织管理', icon: 'shop' },
        { path: '/platform/modules', title: '模块管理', icon: 'app' },
        { path: '/platform/users', title: '用户管理', icon: 'user' }
      ]
    })
  }

  const merchantMenus: Record<string, MenuItem> = {
    rules: {
      path: '/merchant/rules',
      title: '规则配置',
      icon: 'setting',
      children: ruleChildren,
    },
    shops: {
      path: '/merchant/shops',
      title: '分支组织管理',
      icon: 'home',
    },
    employees: {
      path: '/merchant/employees',
      title: userType.value === USER_TYPES.MERCHANT_ADMIN ? '员工与角色' : '员工管理',
      icon: 'usergroup',
      children:
        userType.value === USER_TYPES.MERCHANT_ADMIN
          ? [
              { path: '/merchant/employees', title: '员工管理', icon: 'user' },
              { path: '/merchant/roles', title: '角色管理', icon: 'setting' },
            ]
          : undefined,
    },
    members: {
      path: '/merchant/members',
      title: '会员管理',
      icon: 'user',
    },
    points: {
      path: '/merchant/points/records',
      title: '积分流水',
      icon: 'wallet',
    },
    mall: {
      path: '/merchant/mall',
      title: '积分商城',
      icon: 'shop',
      children: [
        { path: '/merchant/mall/products', title: '商品管理', icon: 'gift' },
        { path: '/merchant/mall/orders', title: '订单管理', icon: 'shopping-cart' },
      ],
    },
  sms: {
    path: '/merchant/sms',
    title: '短信管理 · EM',
    icon: 'mail',
    children: [
      { path: '/merchant/sms', title: '网关配置', icon: 'setting' },
      { path: '/merchant/sms/batch', title: '批量发送', icon: 'mail' },
    ],
  },
  child: {
    path: '/merchant/children',
    title: '孩子管理 · EM',
    icon: 'usergroup-add',
  },
  subjectDocs: {
    path: '/merchant/subject-docs',
    title: '学科资料管理 · EM',
    icon: 'book',
  },
  serviceAppointments: {
    path: '/merchant/service-appointments',
    title: '服务预约管理 · EM',
    icon: 'calendar',
  },
  outstandingStudents: {
    path: '/merchant/outstanding-students',
    title: '优秀学员管理 · EM',
    icon: 'user-avatar',
  },
  news: {
    path: '/merchant/news',
    title: '最新动态管理 · EM',
    icon: 'file-image',
  },
  recommendedCourses: {
    path: '/merchant/recommended-courses',
    title: '推荐课程管理 · EM',
    icon: 'book',
  },
  homeSecondaryPages: {
    path: '/merchant/home-secondary-pages',
    title: '首页管理 · EM',
    icon: 'view-list',
  },
  educationBookings: {
    path: '/merchant/education-bookings',
    title: '学校预约管理 · EM',
    icon: 'calendar',
  },
  lectureBookings: {
    path: '/merchant/lecture-bookings',
    title: '讲座报名管理 · EM',
    icon: 'calendar',
  },
  higherEducationBookings: {
    path: '/merchant/higher-education-bookings',
    title: '升学规划预约管理 · EM',
    icon: 'calendar',
  },
  familyEducationForums: {
    path: '/merchant/family-education-forums',
    title: '论坛管理 · EM',
    icon: 'chat-bubble',
  },
  homeCarousels: {
    path: '/merchant/home-carousels',
    title: '轮播图管理 · EM',
    icon: 'image',
  },
  }

  if (userType.value !== USER_TYPES.PLATFORM_ADMIN) {
    const moduleMenuPairs: Array<[string, MenuItem]> = [
      ['module_rules', merchantMenus.rules],
      ['module_shop', merchantMenus.shops],
      ['module_employee', merchantMenus.employees],
      ['module_member', merchantMenus.members],
      ['module_points', merchantMenus.points],
      ['module_mall', merchantMenus.mall],
      ['module_sms', merchantMenus.sms],
      ['module_child', merchantMenus.child],
      ['module_subject_document', merchantMenus.subjectDocs],
      ['module_service_appointment', merchantMenus.serviceAppointments],
      ['module_outstanding_student', merchantMenus.outstandingStudents],
      ['module_news', merchantMenus.news],
      ['module_recommended_courses', merchantMenus.recommendedCourses],
      ['module_home_management', merchantMenus.homeSecondaryPages],
      ['module_education_booking', merchantMenus.educationBookings],
      ['module_lecture_booking', merchantMenus.lectureBookings],
      ['module_higher_education_booking', merchantMenus.higherEducationBookings],
      ['module_forum_management', merchantMenus.familyEducationForums],
      ['module_home_carousel', merchantMenus.homeCarousels],
    ]
    moduleMenuPairs.forEach(([moduleCode, menu]) => {
      if (hasModule(moduleCode)) items.push(menu)
    })
  }

  return items
})

const setDefaultExpanded = () => {
  const defaults: string[] = []
  if (userType.value === USER_TYPES.PLATFORM_ADMIN) {
    defaults.push('/platform')
  }
  if (userType.value === USER_TYPES.MERCHANT_ADMIN) {
    defaults.push('/merchant/rules', '/merchant/mall')
  } else if (userType.value === USER_TYPES.BRANCH_MANAGER) {
    defaults.push('/merchant/rules', '/merchant/mall')
  } else if (userType.value === USER_TYPES.BRANCH_ADMINISTRATIVE) {
    defaults.push('/merchant/mall')
  }
  expandedMenus.value = defaults
}

const findParentMenu = (items: MenuItem[], targetPath: string): string | null => {
  for (const item of items) {
    if (item.children?.some(child => targetPath.startsWith(child.path))) {
      return item.path
    }
    if (item.children) {
      const nested = findParentMenu(item.children, targetPath)
      if (nested) {
        return item.path
      }
    }
  }
  return null
}

const ensureParentExpanded = (path: string) => {
  const parent = findParentMenu(menuItems.value, path)
  if (parent && !expandedMenus.value.includes(parent)) {
    expandedMenus.value = Array.from(new Set([...expandedMenus.value, parent]))
  }
}

watch(
  () => userType.value,
  () => {
    setDefaultExpanded()
    ensureParentExpanded(route.path)
  },
  { immediate: true }
)

watch(() => route.path, (newPath) => {
  ensureParentExpanded(newPath)
}, { immediate: true })

const activeMenu = computed(() => {
  const path = route.path
  const findActiveMenu = (items: MenuItem[]): string => {
    let matched = ''
    for (const item of items) {
      if (path === item.path) {
        return item.path
      }
      if (item.children?.length) {
        const childMatch = findActiveMenu(item.children)
        if (childMatch) {
          return childMatch
        }
      }
      if (
        path.startsWith(item.path) &&
        item.path !== '/' &&
        !path.startsWith(`${item.path}/`)
      ) {
        if (item.path.length > matched.length) {
          matched = item.path
        }
      }
    }
    return matched
  }
  return findActiveMenu(menuItems.value)
})

const handleMenuChange = (value: string) => {
  if (value !== route.path) {
    router.push(value)
  }
}
</script>

<style lang="less" scoped>
.merchant-info {
  padding: var(--td-spacer-m);
  border-top: 1px solid var(--td-border-level-1-color);
  background: var(--td-bg-color-container-hover);
  border-radius: var(--td-radius-medium);
  
  .merchant-name {
    font-size: var(--td-font-size-s);
    font-weight: 500;
    color: var(--td-text-color-primary);
    margin-bottom: var(--td-spacer-xxs);
  }
  
  .merchant-subtext {
    font-size: var(--td-font-size-xs);
    color: var(--td-text-color-secondary);
    line-height: 1.4;
  }
}
</style>
